import React, { useState, useRef, useEffect } from 'react';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  quickReplies?: string[];
}

const Chatbox: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: 'Hello! I\'m Tuesday Gonzales, your IbayTech AI Assistant! 👋\n\n**Welcome to IbayTech** - Technology for Tomorrow! 🚀\n\nWe\'re based in Metro Manila and Iba, Zambales, empowering local communities through innovative digital solutions since 2022.\n\nHow can I help you today?',
      sender: 'ai',
      timestamp: new Date(),
      quickReplies: ['Our Services 🛠️', 'Get Pricing 💰', 'Contact Us 📞', 'About IbayTech 🏢']
    }
  ]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Helper function to get appropriate quick replies based on context
  const getQuickReplies = (responseText: string): string[] => {
    const text = responseText.toLowerCase();
    
    if (text.includes('service') && text.includes('offers')) {
      return ['Web Development 🌐', 'Mobile Apps 📱', 'IT Consulting 💼', 'Get Quote 💰'];
    }
    if (text.includes('web') || text.includes('mobile') || text.includes('technology')) {
      return ['Get Quote 💰', 'See Portfolio 🎨', 'Contact Us 📞', 'Other Services 🛠️'];
    }
    if (text.includes('contact') || text.includes('email') || text.includes('phone')) {
      return ['Our Services �️', 'Get Quote 💰', 'About IbayTech 🏢', 'Portfolio 🎨'];
    }
    if (text.includes('pricing') || text.includes('quote') || text.includes('cost')) {
      return ['Contact Us 📞', 'Our Services 🛠️', 'About IbayTech 🏢', 'Portfolio 🎨'];
    }
    
    // Default quick replies
    return ['Our Services 🛠️', 'Get Quote 💰', 'Contact Us 📞', 'About IbayTech 🏢'];
  };

  // Real AI integration with OpenAI
  const getAIResponse = async (userMessage: string): Promise<{ text: string; quickReplies?: string[] }> => {
    try {
      // Call OpenAI API
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
          context: {
            name: 'Tuesday Gonzales',
            company: 'IbayTech',
            role: 'AI Assistant for IbayTech website',
            services: ['Web Development', 'Mobile App Development', 'IT Consulting', 'Digital Solutions'],
            personality: 'Friendly, helpful, professional with a touch of humor'
          }
        })
      });

      if (!response.ok) {
        throw new Error('AI API request failed');
      }

      const data = await response.json();
      const aiText = data.response || data.message || 'I apologize, but I didn\'t receive a proper response. Could you please try again?';
      
      return {
        text: aiText,
        quickReplies: getQuickReplies(aiText)
      };

    } catch (error) {
      console.error('AI API Error:', error);
      
      // Fallback to rule-based responses if AI fails
      const message = userMessage.toLowerCase();
      
      if (message.includes('hello') || message.includes('hi') || message.includes('hey') || message.includes('kumusta')) {
        return {
          text: "Hello! I'm Tuesday Gonzales, your IbayTech AI Assistant! 👋 Welcome to IbayTech! How can I help you today?",
          quickReplies: ['Our Services 🛠️', 'Get Pricing 💰', 'Contact Us 📞', 'About IbayTech �']
        };
      }

      if (message.includes('service') || message.includes('Our Services')) {
        return {
          text: "IbayTech offers these amazing solutions:\n\n🛠️ **Custom Software Development**\nTailored solutions for your specific business needs\n\n📱 **Ibay App**\nThe first online marketplace in Iba, Zambales since 2022\n\n🏛️ **LGU Services Portal**\nDigital automation for government services - making bureaucracy faster and transparent\n\nWhich service interests you?",
          quickReplies: ['Custom Software 🛠️', 'Ibay App 📱', 'LGU Services 🏛️', 'Get Quote 💰']
        };
      }

      if (message.includes('Contact Us') || message.includes('contact')) {
        return {
          text: "You can reach IbayTech here:\n\n📧 **Email:** support.team@ibaytechcorp.com\n📱 **Phone:** +63 917 127 4229\n📍 **Address:** G/F Livelihood Center Bldg.\nR. Magsaysay Ave. Zone IV Sagapan Poblacion\nIba, Zambales, Philippines\n\n🌐 Visit our contact page for more details\n\nWe respond within 24 hours! 🚀",
          quickReplies: ['Our Services 🛠️', 'Get Quote 💰', 'About IbayTech 🏢', 'Portfolio 🎨']
        };
      }

      // Specific service responses
      if (message.includes('Custom Software') || message.includes('web') || message.includes('website') || message.includes('development')) {
        return {
          text: "🛠️ **Custom Software Development**\n\nBespoke software solutions designed to meet your specific business needs!\n\n**What We Offer:**\n• Web application development\n• Mobile app development\n• API integration\n• Database design & optimization\n\n**Benefits:**\n• Perfect fit for your business\n• Scalable architecture\n• Ongoing technical support\n• Future-ready technology\n\n**Timeline:** 4-12 weeks\n**Pricing:** ₱25,000 - ₱500,000+ based on requirements\n\nReady to discuss your project?",
          quickReplies: ['Get Quote 💰', 'Other Services 🛠️', 'Contact Us 📞', 'Portfolio 🎨']
        };
      }

      if (message.includes('Ibay App') || message.includes('marketplace') || message.includes('mobile')) {
        return {
          text: "📱 **Ibay App - Mobile Marketplace**\n\n*Get your Favorite Stuff from your Favorite Stores!*\n\nThe **first online marketplace in Iba, Zambales since 2022**, connecting local businesses with customers.\n\n**Features:**\n• User-friendly mobile interface\n• Merchant store integration\n• Real-time order tracking\n\n**Benefits for Businesses:**\n• Boost local business visibility\n• Increase sales revenue\n• Build customer loyalty\n• Streamline operations\n• Expand market reach\n\nInterested in similar marketplace solution?",
          quickReplies: ['Get Quote 💰', 'Other Services 🛠️', 'Contact Us 📞', 'Portfolio 🎨']
        };
      }

      if (message.includes('LGU Services') || message.includes('government') || message.includes('permit')) {
        return {
          text: "🏛️ **LGU Services Portal**\n\n*Ease of Doing Business a step further!*\n\nStreamlining government services through digital automation, making bureaucratic processes faster and more transparent.\n\n**Available Services:**\n• Online Business Permit Applications\n• Online Community Tax\n• Online Barangay Clearance\n• Online Occupational Permit\n• Online Building Permit\n\n**Benefits:**\n• Reduce processing time by 80%\n• Eliminate paper-based workflows\n• Improve transparency\n• Enhance citizen satisfaction\n\nNeed similar government automation?",
          quickReplies: ['Get Quote 💰', 'Other Services 🛠️', 'Contact Us 📞', 'Portfolio 🎨']
        };
      }

      // Individual project details
      if (message.includes('RPT-Abra') || message.includes('property tax')) {
        return {
          text: "🏢 **RPT-Abra - Real Property Tax System (2025)**\n\nA comprehensive platform designed to streamline property tax declaration, assessment, and records management. The system enhances transparency and efficiency for both government agencies and taxpayers, offering online and on-premise access for various stakeholders involved in the property tax process.\n\n**Key Features:**\n• Database Management\n• User-Friendly Interface\n• Increased Efficiency\n• Improved Accuracy\n• Greater Transparency\n\n**Status:** ✅ Live\n**Category:** Government Digital Services\n\nThis project showcases our expertise in government digitalization and public service automation.",
          quickReplies: ['Other Projects 🎨', 'Similar System 💰', 'Contact Us 📞', 'Our Services 🛠️']
        };
      }

      if (message.includes('EMR System') || message.includes('healthcare') || message.includes('medical records')) {
        return {
          text: "🏥 **EMR System - Healthcare Management (2025)**\n\nElectronic Medical Records system for healthcare facilities. Digitizing patient records and streamlining medical workflows for better healthcare delivery.\n\n**Comprehensive Features:**\n• Patient Profile & Records\n• Admin Dashboard\n• Medical History\n• Laboratory Management\n• Pharmacy & Supplies\n• Emergency Services\n• Consultation Scheduling\n• Reporting & Analytics\n\n**Status:** 🔄 Under Development\n**Category:** Healthcare Technology\n\nThis system will revolutionize healthcare management by providing a complete digital solution for medical facilities.",
          quickReplies: ['Other Projects 🎨', 'Healthcare Solution 💰', 'Contact Us 📞', 'Our Services 🛠️']
        };
      }

      if (message.includes('about') || message.includes('company') || message.includes('tungkol') || message.includes('About IbayTech')) {
        return {
          text: `Let me tell you about IbayTech! 🏢✨\n\n**IbayTech** is an IT Company based in Metro Manila and Iba, Zambales dedicated to providing digital transparency for public governance, upgrade management and community solutions.\n\n📍 **Founded:** 2022\n🏢 **Offices:** Metro Manila & Iba, Zambales\n🎯 **Tagline:** Technology for Tomorrow\n\n**🎯 Our Mission:**\nTo empower local communities through innovative technology solutions that enhance daily life and drive economic growth.\n\n**💡 Our Vision:**\nTo be the leading technology partner for communities, creating a digitally connected and prosperous future.\n\n**⭐ Our Core Values:**\n🚀 **Innovation** - Cutting-edge solutions for real-world problems\n🤝 **Community** - Local focus with global standards\n🏆 **Excellence** - Quality and reliability in every project\n\nBy the way, I'm Tuesday Gonzales! 👋 Your friendly IbayTech AI Assistant!`,
          quickReplies: ['Our Services 🛠️', 'Get Quote 💰', 'Contact Us 📞', 'Portfolio 🎨']
        };
      }

      if (message.includes('portfolio') || message.includes('work') || message.includes('projects') || message.includes('Portfolio') || message.includes('See Portfolio')) {
        return {
          text: "🎨 **Our Portfolio**\n\nInnovative projects that make a difference in Iba, Zambales and beyond:\n\n**🟢 LIVE PROJECTS:**\n\n📱 **Ibay App (2022)** - Mobile Marketplace\nFirst online marketplace in Iba, Zambales connecting local stores with customers. A comprehensive platform that bridges the gap between local businesses and customers.\n*Features: Real-time Order Tracking, Secure Payment System, Multi-vendor Support*\n\n🏛️ **LGU Services Portal (2025)** - Government Platform  \nDigital platform for automated government services and permit applications. Streamlining bureaucratic processes for citizens and government offices.\n*Features: Online Business Permit, Community Tax, Barangay Clearance, Occupational Permit, Building Permit*\n*🌐 Live at: https://ibaservicesportal.com/*\n\n🏢 **RPT-Abra (2025)** - Real Property Tax System\nComprehensive platform designed to streamline property tax declaration, assessment, and records management. Enhances transparency and efficiency for government agencies and taxpayers.\n*Features: Database Management, User-Friendly Interface, Increased Efficiency, Improved Accuracy, Greater Transparency*\n\n**🔄 UNDER DEVELOPMENT:**\n\n🏥 **EMR System (2025)** - Healthcare Management\nElectronic Medical Records system for healthcare facilities. Digitizing patient records and streamlining medical workflows for better healthcare delivery.\n*Features: Patient Profile & Records, Admin Dashboard, Medical History, Laboratory Management, Pharmacy & Supplies, Emergency Services, Consultation Scheduling, Reporting & Analytics*\n\nWant to see your project here next?",
          quickReplies: ['Get Quote 💰', 'Our Services 🛠️', 'Contact Us 📞', 'About IbayTech 🏢']
        };
      }

      // Generic fallback
      return {
        text: `I'm Tuesday Gonzales! 😊 I understand you're asking about: "${userMessage}"\n\nI can help you with IbayTech's:\n• Services & solutions 🛠️\n• Pricing information 💰\n• Contact details 📞\n• Company information 🏢\n\nWhat would you like to know more about?`,
        quickReplies: ['Our Services 🛠️', 'Get Quote 💰', 'Contact Us 📞', 'About IbayTech 🏢']
      };
    }
  };

  const sendMessage = async () => {
    if (!inputText.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputText,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = inputText;
    setInputText('');
    setIsLoading(true);

    // Call AI API
    try {
      const aiResponse = await getAIResponse(currentInput);
      
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: aiResponse.text,
        sender: 'ai',
        timestamp: new Date(),
        quickReplies: aiResponse.quickReplies
      };

      setMessages(prev => [...prev, aiMessage]);
      setIsLoading(false);
    } catch (error) {
      console.error('AI Error:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: 'Sorry, I encountered an error. Please try again later. 😅',
        sender: 'ai',
        timestamp: new Date(),
        quickReplies: ['Our Services 🛠️', 'Contact Us 📞', 'Try Again 🔄']
      };
      setMessages(prev => [...prev, errorMessage]);
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleQuickReply = async (reply: string) => {
    setInputText('');
    // Auto-send the quick reply
    const userMessage: Message = {
      id: Date.now().toString(),
      text: reply,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      const aiResponse = await getAIResponse(reply);
      
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: aiResponse.text,
        sender: 'ai',
        timestamp: new Date(),
        quickReplies: aiResponse.quickReplies
      };

      setMessages(prev => [...prev, aiMessage]);
      setIsLoading(false);
    } catch (error) {
      console.error('AI Error:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: 'Sorry, I encountered an error. Please try again later. 😅',
        sender: 'ai',
        timestamp: new Date(),
        quickReplies: ['Our Services 🛠️', 'Contact Us 📞', 'Try Again 🔄']
      };
      setMessages(prev => [...prev, errorMessage]);
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed bottom-20 right-4 z-50">
      {/* Chat Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white p-4 rounded-full shadow-lg transition-all duration-300 hover:scale-110 animate-pulse"
        title="Chat with Tuesday Gonzales - IbayTech AI"
      >
        {isOpen ? (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="absolute bottom-16 right-0 w-80 h-96 bg-white rounded-xl shadow-2xl border border-gray-200 flex flex-col animate-in slide-in-from-bottom-5 duration-300 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 rounded-t-xl">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg">Tuesday Gonzales</h3>
                <p className="text-xs opacity-90 flex items-center">
                  <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></span>
                  IbayTech AI Assistant
                </p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="hover:bg-white/20 p-1 rounded-full transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-gray-50 to-white">
            {messages.map((message) => (
              <div key={message.id} className="space-y-2">
                <div className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-xs p-3 rounded-xl shadow-sm transition-all duration-200 hover:shadow-md ${
                      message.sender === 'user'
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-br-sm'
                        : 'bg-white text-gray-800 rounded-bl-sm border border-gray-200'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-line leading-relaxed">{message.text}</p>
                    <p className={`text-xs mt-2 ${
                      message.sender === 'user' ? 'text-blue-100' : 'text-gray-500'
                    }`}>
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
                
                {/* Quick Reply Buttons */}
                {message.sender === 'ai' && message.quickReplies && (
                  <div className="flex flex-wrap gap-2 mt-2 pl-0">
                    {message.quickReplies.map((reply: string, index: number) => (
                      <button
                        key={index}
                        onClick={() => handleQuickReply(reply)}
                        className="bg-gradient-to-r from-blue-100 to-purple-100 hover:from-blue-200 hover:to-purple-200 text-blue-800 text-xs px-3 py-2 rounded-full border border-blue-200 transition-all duration-200 hover:shadow-md disabled:opacity-50"
                        disabled={isLoading}
                      >
                        {reply}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-800 p-3 rounded-xl rounded-bl-sm border border-gray-200 shadow-sm">
                  <div className="flex space-x-1 items-center">
                    <span className="text-xs text-gray-500 mr-2">AI is typing</span>
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-gray-200 bg-white rounded-b-xl">
            <div className="flex space-x-2">
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me anything about IbayTech..."
                className="flex-1 p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-gray-800"
                disabled={isLoading}
                maxLength={500}
              />
              <button
                onClick={sendMessage}
                disabled={isLoading || !inputText.trim()}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-gray-400 disabled:to-gray-400 text-white p-3 rounded-xl transition-all duration-200 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              Powered by IbayTech • {inputText.length}/500
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chatbox;